import React from 'react'
import classnames from 'classnames'
import isEqual from 'lodash/isEqual'
import { createSelector } from 'reselect'
import { Dropdown, Input, Popup, Form } from 'semantic-ui-react'

import { formatDate, parseDate } from 'react-day-picker/moment'
import { ComponentContainer } from 'shared/StyledComponents'
import { LKTextArea, LKDatePicker, Radio, RadioGroup, Checkbox } from 'loankeeper-common/components'
import { translate as translateHelper, getValidationClasses } from 'loankeeper-common/helpers'
import { LKDate, DATE_FORMAT, monthsArray, weeksArray } from 'loankeeper-common/helpers/Date'

const popperModifiers = { preventOverflow: { enabled: false } }

const transformOptions = createSelector(
  (data) => data,
  (options) =>
    options.map(({ value, ...rest }) => ({
      value,
      ...rest,
    })),
)

const translate = (text) => translateHelper(`frm_date_picker-${text}`)
const translateLKMessage = (key) => translateHelper(`LKMESSAGE-${key}`)

const getMonths = () => monthsArray.map((month) => month.toLowerCase()).map((month) => translate(month))

const getShortWeeks = () => weeksArray.map((week) => week.toLowerCase()).map((week) => translate(`${week}_short`))

// const getValidationClasses = (error, required, touched) => {
//   if (required) {
//     return classnames({
//       'failed-field': error && touched,
//       'passed-field': !touched,
//     })
//   }

//   if (touched && error) {
//     return 'failed-field'
//   }

//   return ''
// }

class FormField extends React.Component {
  state = { error: '', searchQuery: '', searchInput: '', focused: false }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextState !== this.state) return true

    const { form, name, options, ...rest } = this.props

    const { form: nform, name: nname, options: noptions, ...nrest } = nextProps

    if (name !== nname) return true

    if (form.values[name] !== nform.values[name]) return true

    if (form.errors[name] !== nform.errors[name]) return true

    if (form.touched[name] !== nform.touched[name]) return true

    if (options !== noptions) return true

    return !isEqual(rest, nrest)
  }

  getDayPickerProps = () => {
    const { dayPickerProps: mergeProps } = this.props
    const { form, name, from } = this.props
    const value = form.values[name]

    const dayPickerProps = {
      firstDayOfWeek: 1,
      onDayClick: this.handleDayClicked,
      selectedDays: parseDate(value, DATE_FORMAT),
      month: parseDate(value, DATE_FORMAT),
      months: getMonths(),
      weekdaysShort: getShortWeeks(),
      disabledDays: from ? [{ before: LKDate.from(from).toDate() }] : undefined,
      onFocus: () => document.getElementsByName(name)[0].focus(),
      ...mergeProps,
    }

    const { min, max } = this.props

    if (min) {
      dayPickerProps.disabledDays = { before: min }
      dayPickerProps.fromMonth = min
    }

    if (max) {
      dayPickerProps.disabledDays = { after: max }
      dayPickerProps.toMonth = max
    }

    return dayPickerProps
  }

  handleDropDownSearchChange = (e, { searchQuery }) => {
    this.setState({ searchQuery, searchInput: searchQuery })
  }

  handleDropDownChange = (e, data) => {
    let searchInput = ''

    if (this.props.options && this.props.options.find((x) => x.value === data.value)) {
      searchInput = this.props.options.find((x) => x.value === data.value).text
    } else {
      searchInput = data.value
    }

    this.setState({ searchInput })
    this.handleChange(e, data)
  }

  handleKeyPress = (event) => {
    const { target, key } = event

    const { integer, positive } = this.props

    if (
      (isNaN(target.value) || isNaN(key) || key === ' ') &&
      key !== 'Enter' &&
      target.value !== '.' &&
      target.value !== '-' &&
      (key !== '.' || integer) &&
      (key !== '-' || positive)
    ) {
      this.setState({
        error: translateLKMessage('HINT_ERR_NUMBER_ONLY'),
      })

      setTimeout(() => this.setState({ error: '' }), 3000)
    } else if (this.state.error) {
      this.setState({
        error: '',
      })
    }
  }

  handleDateChange = ({ target: { value } }) => {
    const { name, form, onChange, onChanged } = this.props

    let normalized = value

    if (value.split('.').length < 3) {
      if ((value.length === 2 || value.length === 5) && !value.endsWith('.')) {
        normalized += '.'
      } else if (value.length === 3 && !value.includes('.')) {
        normalized = value.split('')
        normalized.splice(2, 0, '.')
        normalized = normalized.join('')
      } else if (value.length === 6 && !value.endsWith('.')) {
        normalized = value.split('')
        normalized.splice(5, 0, '.')
        normalized = normalized.join('')
      }
    }

    if (onChange) {
      onChange(normalized, name)
    } else {
      form.handleDateChange(name, normalized, onChanged)
    }
  }

  handleDayClicked = (date) => {
    const { name, form, onChange, onChanged } = this.props
    const value = formatDate(date, DATE_FORMAT)
    if (onChange) {
      onChange(value, name)
    } else {
      form.handleDateChange(name, value, onChanged)
    }

    if (this.props.onDayClick) this.props.onDayClick(value)
  }

  handleDateKeyPress = (e) => {
    if (e.key !== '.' && isNaN(e.key)) e.preventDefault()

    const { value } = e.target

    if (e.key === '.' && (value.endsWith('.') || value.split('.').length > 2)) e.preventDefault()

    if (e.target.selectionStart === e.target.selectionEnd && value && value.length > 9) {
      e.preventDefault()
    }

    if (this.props.onKeyPress) this.props.onKeyPress(e)
  }

  handleDateBlur = (event) => {
    this.props.form.handleBlur(event, 'date')

    if (this.props.onBlur) this.props.onBlur(event)
  }

  handleChange = (event, data) => {
    const { form, onChange, onChanged, dontParse, allowIndeterminate } = this.props

    form.handleChange(event, { ...data, dontParse, allowIndeterminate }, onChange, onChanged)
  }

  handleFocus = () => {
    this.setState({ focused: true })
  }

  handleBlur = (event) => {
    this.setState({ focused: false })

    this.props.form.handleBlur(event)
  }

  render() {
    const {
      showOn,
      type,
      name,
      form,
      autoWidth,
      flex,
      stepper,
      min,
      max,
      maxLength,
      inputProps,
      upward,
      floatLeft,
      menuWidth,
      right,
      onChange,
      integer,
      positive,
      scale,
      required,
      onChanged,
      vertical,
      horizontal,
      options,
      errorDown,
      format,
      isPersonalNo,
      height,
      noResize,
      small,
      disabled,
      label,
      ...rest
    } = this.props

    let value = form.values[name]
    if (value === undefined) value = type === 'checkbox' ? false : ''

    const hasError = form.hasError(name)

    const error = form.errors[name]

    const brandingClass = 'lk-form-field'

    const classes = classnames(brandingClass, 'LKError', {
      'LKError--hidden': (!hasError || error === true) && !this.state.error,
    })

    const touched = form.touched[name]

    const inputClasess = getValidationClasses(hasError, required, touched)

    switch (type) {
      case 'checkbox':
        return (
          <Checkbox
            name={name}
            onChange={(value) => {
              form.changeValue(name, value, onChanged)

              if (onChange) {
                onChange(value, name)
              }
            }}
            className={classnames(brandingClass, rest.className)}
            checked={value}
            disabled={disabled}
            label={label ?? ''}
            {...rest}
          />
        )
      case 'select':
        return (
          <ComponentContainer
            flex={flex}
            autoWidth={autoWidth}
            floatLeft={floatLeft}
            menuWidth={menuWidth}
            className={brandingClass}
            dropdown
          >
            <Dropdown
              name={name}
              value={value}
              disabled={disabled}
              options={transformOptions(options)}
              error={touched && (hasError || (error && error.trim() !== ''))}
              onBlur={form.handleBlur}
              upward={upward}
              className={inputClasess}
              selection
              fluid
              onSearchChange={this.handleDropDownSearchChange}
              onChange={this.handleDropDownChange}
              text={this.props.search && this.input === document.activeElement && this.state.searchInput ? ' ' : ''}
              {...rest}
            />
          </ComponentContainer>
        )
      case 'date':
        return (
          <ComponentContainer className={brandingClass} autoWidth={autoWidth}>
            <Popup
              className={classes}
              on="focus"
              popperDependencies={[error, hasError]}
              popperModifiers={popperModifiers}
              trigger={
                <Form>
                  <LKDatePicker
                    right={right}
                    value={value}
                    placeholder={DATE_FORMAT}
                    format={DATE_FORMAT}
                    formatDate={formatDate}
                    parseDate={parseDate}
                    dayPickerProps={this.getDayPickerProps()}
                    inputProps={{
                      fieldtype: 'date',
                      onChange: this.handleDateChange,
                      onKeyPress: this.handleDateKeyPress,
                      onBlur: this.handleDateBlur,
                      autoComplete: 'off',
                      name,
                      disabled,
                      ...inputProps,
                    }}
                    upward={upward}
                    error={hasError}
                    {...rest}
                  />
                </Form>
              }
            >
              {error}
            </Popup>
          </ComponentContainer>
        )

      case 'textarea':
        return (
          <ComponentContainer className={brandingClass} small={small} height={height} noResize={noResize}>
            <Form>
              <LKTextArea
                name={name}
                value={value}
                disabled={disabled}
                onChange={this.handleChange}
                onBlur={form.handleBlur}
                {...rest}
              />
            </Form>
          </ComponentContainer>
        )

      case 'radiogroup':
        return (
          <RadioGroup
            name={name}
            selectedValue={value}
            className={classnames(brandingClass, 'u-flex u-fluid u-spaceBetween', {
              'u-directionColumn': vertical,
              'u-alignCenter u-directionRow': horizontal,
            })}
            onChange={(d) => {
              form.changeValue(name, d, onChanged)
            }}
          >
            {options.map((option) => (
              <Radio key={option.value} {...option} />
            ))}
          </RadioGroup>
        )
      case 'number':
        return (
          <ComponentContainer className={brandingClass} input autoWidth={autoWidth} flex={flex}>
            <Popup
              className={classes}
              on={showOn ? showOn : 'focus'}
              popperDependencies={[this.state.error, error, hasError]}
              popperModifiers={popperModifiers}
              trigger={
                <Input
                  name={name}
                  fieldtype="number"
                  type={stepper ? 'number' : undefined}
                  min={min}
                  max={max}
                  value={
                    format && !this.state.focused && value !== ''
                      ? parseFloat(value)
                          .toFixed(scale || 2)
                          .replace(/(\d)(?=(\d{3})+\.)/g, '$1,')
                      : value
                  }
                  onKeyPress={this.handleKeyPress}
                  onChange={this.handleChange}
                  onFocus={this.handleFocus}
                  onBlur={this.handleBlur}
                  error={hasError}
                  scale={scale}
                  className={inputClasess}
                  maxLength={maxLength}
                  disabled={disabled}
                  fluid
                  {...rest}
                />
              }
            >
              {this.state.error || error}
            </Popup>
          </ComponentContainer>
        )
      default:
        return (
          <ComponentContainer className={brandingClass} input autoWidth={autoWidth} flex={flex}>
            <Popup
              className={classes}
              on="focus"
              popperDependencies={[error, hasError]}
              popperModifiers={popperModifiers}
              trigger={
                <Input
                  type={type}
                  name={name}
                  value={value}
                  onChange={this.handleChange}
                  onBlur={form.handleBlur}
                  error={hasError}
                  className={inputClasess}
                  maxLength={maxLength}
                  disabled={disabled}
                  fluid
                  {...rest}
                />
              }
            >
              {error}
            </Popup>
          </ComponentContainer>
        )
    }
  }
}

export default FormField
